import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges
} from '@angular/core';
import {
    EmailAutomationServiceProxy, SetTaskClientInput, TaskClientCandidateDto
} from '@shared/service-proxies/service-proxies';

/**
 * Client REVIEW picker (R3d) for a flagged email task whose sender matched MORE THAN ONE distinct client. The
 * matcher does not guess in that case - it links no client and stores the candidates. This shows the amber
 * "N possible clients - tap to select" cue; picking one calls SetTaskClient (no-clobber: links the task's client
 * and retires the candidates) and tells the host to reflect it.
 *
 * Self-contained + OnPush: only renders for a task that has NO client yet AND has candidates - otherwise it emits
 * hasCandidatesChange(false) so the host keeps its normal client/contact display.
 */
@Component({
    selector: 'vf-task-client-review',
    templateUrl: './task-client-review.component.html',
    styleUrls: ['./task-client-review.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskClientReviewComponent implements OnChanges {
    @Input() taskId: number | undefined;
    /** True when the task already has a client linked; then this picker is irrelevant (renders nothing). */
    @Input() hasClient: boolean | undefined;

    /** A candidate was confirmed - the host should reflect the chosen client. */
    @Output() confirmed = new EventEmitter<{ entityType: number; clientId: number; clientName: string }>();
    /** Whether there are candidates to review (the host can hide/adjust its normal client display while true). */
    @Output() hasCandidatesChange = new EventEmitter<boolean>();

    candidates: TaskClientCandidateDto[] = [];
    open = false;
    saving = false;
    errorMessage = '';

    constructor(private _email: EmailAutomationServiceProxy, private _cdr: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.taskId || changes.hasClient) {
            this.candidates = [];
            this.open = false;
            this.errorMessage = '';
            this.hasCandidatesChange.emit(false);
            // Candidates only matter while the task has no client assigned.
            if (this.taskId && !this.hasClient) {
                this.load();
            }
        }
    }

    get count(): number {
        return this.candidates.length;
    }

    toggle(): void {
        this.open = !this.open;
    }

    close(): void {
        this.open = false;
    }

    trackByCandidateId(_index: number, candidate: TaskClientCandidateDto): number {
        return candidate ? candidate.id : _index;
    }

    confirm(candidate: TaskClientCandidateDto): void {
        if (this.saving || !this.taskId || !candidate) {
            return;
        }
        this.saving = true;
        this.errorMessage = '';

        const input = new SetTaskClientInput();
        input.taskId = this.taskId;
        input.entityType = candidate.entityType;
        input.clientId = candidate.clientId;

        this._email.setTaskClient(input).subscribe(
            res => {
                this.saving = false;
                if (res && res.responseData && !res.isError) {
                    this.open = false;
                    this.candidates = [];
                    this.hasCandidatesChange.emit(false);
                    this.confirmed.emit({ entityType: candidate.entityType, clientId: candidate.clientId, clientName: candidate.clientName });
                } else {
                    this.errorMessage = (res && res.errorMessage) || 'Could not set the client.';
                }
                this._cdr.markForCheck();
            },
            () => {
                this.saving = false;
                this.errorMessage = 'Could not set the client.';
                this._cdr.markForCheck();
            }
        );
    }

    private load(): void {
        const id = this.taskId!;
        this._email.getTaskClientCandidates(id).subscribe(
            res => {
                this.candidates = (res && res.responseData) || [];
                this.hasCandidatesChange.emit(this.candidates.length > 0);
                this._cdr.markForCheck();
            },
            () => {
                this.candidates = [];
                this.hasCandidatesChange.emit(false);
                this._cdr.markForCheck();
            }
        );
    }
}
